/**
 * `document.cookie` access — behaviour-preserving replacement for the three
 * `cookies-next/client` helpers, dropped because `cookies-next` peer-depends on
 * `next` non-optionally (~152 MB in `node_modules` and the image). It only ever
 * used `cookie`, which we already depend on directly, and routed every attribute
 * through `cookie`'s own `serialize` — so calling `serialize` here is
 * byte-identical.
 *
 * Copied deliberately because observable: `path: "/"` is the default,
 * caller-overridable; delete writes an empty value with `maxAge: -1` keeping the
 * caller's `domain`/`secure`/`sameSite` — a delete that drops the domain will
 * not clear a domain-scoped cookie.
 */
import { parse, serialize, type SerializeOptions } from "cookie";

type CookieOptions = SerializeOptions;

const isBrowser = () => typeof window !== "undefined";

export function getCookie(key: string): string | undefined {
  if (!isBrowser()) {
    return undefined;
  }
  return parse(document.cookie)[key];
}

export function setCookie(
  key: string,
  value: string,
  options?: CookieOptions
): void {
  if (!isBrowser()) {
    return;
  }
  document.cookie = serialize(key, value, { path: "/", ...options });
}

export function deleteCookie(key: string, options?: CookieOptions): void {
  setCookie(key, "", { ...options, maxAge: -1 });
}
